const PDF_SAFE_PREFIXES = ['data:image/jpeg', 'data:image/jpg', 'data:image/png'];

const isPdfSafe = (value: string): boolean => PDF_SAFE_PREFIXES.some((prefix) => value.startsWith(prefix));

// react-pdf decodes ONLY JPEG and PNG, so anything else (e.g. a WebP thumbnail captured before
// downscaleImage emitted JPEG) has to be transcoded or it silently renders as nothing.
const transcodeToJpeg = async (dataUrl: string): Promise<string> => {
	try {
		const img = await new Promise<HTMLImageElement>((resolve, reject) => {
			const el = new Image();
			el.onload = () => resolve(el);
			el.onerror = () => reject(new Error('transcodeToJpeg: load failed'));
			el.src = dataUrl;
		});

		const canvas = document.createElement('canvas');
		canvas.width = Math.max(1, img.naturalWidth);
		canvas.height = Math.max(1, img.naturalHeight);

		const ctx = canvas.getContext('2d');
		if (!ctx) return dataUrl;
		ctx.fillStyle = '#FFFFFF';
		ctx.fillRect(0, 0, canvas.width, canvas.height);
		ctx.drawImage(img, 0, 0);

		return canvas.toDataURL('image/jpeg', 0.9);
	} catch {
		return dataUrl;
	}
};

/**
 * Resolve an image src (blob URL or data URL) to a PDF-embeddable data URL. Remote URLs are fetched
 * (react-pdf can trip on cross-origin embedding); any format react-pdf cannot decode is transcoded
 * to JPEG. Falls back to the raw src if the fetch fails.
 */
export const toImageDataUrl = async (src: string): Promise<string> => {
	if (!src) return src;

	if (src.startsWith('data:')) return isPdfSafe(src) ? src : transcodeToJpeg(src);

	try {
		const res = await fetch(src);
		const blob = await res.blob();
		const dataUrl = await new Promise<string>((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => resolve(reader.result as string);
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
		return isPdfSafe(dataUrl) ? dataUrl : transcodeToJpeg(dataUrl);
	} catch {
		return src;
	}
};
